import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { PDFDocument } from "pdf-lib";
import {
  Document,
  Page,
  pdf,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    flexDirection: "row",
    backgroundColor: "#ffffff",
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
});

const MyDocument = ({ content }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.section}>
        <Text>{content}</Text>
      </View>
    </Page>
  </Document>
);

const PriceBook = ({ setIsShowPriceBook }) => {
  const [error, setError] = useState(null);
  const [content, setContent] = useState({});
  const [isPdf, setIsPdf] = useState({});
  const [pdfUrls, setPdfUrls] = useState([]);
  const [htmlContents, setHtmlContents] = useState({});
  const [pdfLoading, setPdfLoading] = useState(true);

  const props = useSelector((state) => state.reducer);

  const iframeRef = useRef(null);

  useEffect(() => {
    const fetchAndValidateContent = async () => {
      if (props.priceBookFile && props.priceBookFile.length > 0) {
        const pdfData = props.priceBookFile;

        const contentPromises = pdfData.map(async (pdf, index) => {
          try {
            const response = await fetch(pdf.path);
            if (!response.ok) {
              if (response.status === 404) {
                return {
                  url: null,
                  content: `The price Sheet file is Invalid. Please update the file in Sensei Connect.`,
                };
              } else {
                setError(
                  `Error fetching content from ${pdf.path}: ${response.statusText}`
                );
                return { url: null, content: null };
              }
            }
            const contentType = response.headers.get("Content-Type");

            if (contentType && contentType.includes("application/pdf")) {
              setIsPdf((prev) => ({ ...prev, [index]: true }));
              return { url: pdf.path, content: null };
            } else if (contentType && contentType.includes("text/html")) {
              const htmlContent = await response.text();
              setIsPdf((prev) => ({ ...prev, [index]: false }));
              return { url: null, content: htmlContent };
            } else {
              setError(`File at ${pdf.path} is neither a valid PDF nor HTML.`);
              return { url: null, content: null };
            }
          } catch (err) {
            setError(`Error fetching content from ${pdf.path}: ${err.message}`);
            return { url: null, content: null };
          }
        });

        const results = await Promise.all(contentPromises);
        const urls = results
          .filter((result) => result.url)
          .map((result) => result.url);
        const contents = results
          .filter((result) => result.content)
          .map((result) => result.content);

        setContent(
          contents.reduce((acc, item, idx) => ({ ...acc, [idx]: item }), {})
        );
        setPdfUrls(urls);
        setHtmlContents(
          contents.reduce((acc, item, idx) => ({ ...acc, [idx]: item }), {})
        );
        setTimeout(() => {
          setPdfLoading(false);
        }, 1000);
      }
    };

    fetchAndValidateContent();
  }, [props.priceBookFile]);

  const fetchPdfData = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch ${url}`);
    return await response.arrayBuffer();
  };

  const combinePdfs = async (urls) => {
    const mergedPdf = await PDFDocument.create();

    for (const url of urls) {
      const pdfData = await fetchPdfData(url);
      const pdfDoc = await PDFDocument.load(pdfData);
      const copiedPages = await mergedPdf.copyPages(
        pdfDoc,
        pdfDoc.getPageIndices()
      );
      copiedPages.forEach((page) => mergedPdf.addPage(page));
    }

    return mergedPdf.save();
  };

  const downloadCombinedPdf = async () => {
    try {
      const files = Array.isArray(props.priceBookFile)
        ? props.priceBookFile
        : Object.values(props.priceBookFile);
      const pdfUrls = files.map((file) => file.path);

      const combinedPdfBytes = await combinePdfs(pdfUrls);

      const blob = new Blob([combinedPdfBytes], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `CombinedPriceBook-${new Date().getTime()}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error combining PDFs:", error);
    }
  };

  const openCombinedPdfInNewTab = async () => {
    try {
      const files = Array.isArray(props.priceBookFile)
        ? props.priceBookFile
        : Object.values(props.priceBookFile);
      const pdfUrls = files.map((file) => file.path);

      const combinedPdfBytes = await combinePdfs(pdfUrls);

      const blob = new Blob([combinedPdfBytes], { type: "application/pdf" });
      const blobUrl = window.URL.createObjectURL(blob);

      window.open(blobUrl, "_blank");

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
    } catch (error) {
      console.error("Error combining PDFs:", error);
    }
  };

  const downloadPDF = async () => {
    downloadCombinedPdf();
  };

  const openNewTab = () => {
    openCombinedPdfInNewTab();
  };

  const generateAndDownloadNewPdf = async (content) => {
    try {
      const pdfBlob = await pdf(<MyDocument content={content} />).toBlob();
      const url = window.URL.createObjectURL(pdfBlob);

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `GeneratedDocument-${new Date().getTime()}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  // Inline styles
  const inlineStyles = {
    bookModalBody: {
      height: "calc(100vh - 60px)", // Adjust based on header height
      display: "flex",
      flexDirection: "column",
    },
    pdfViewer: {
      flex: 1,
      overflow: "auto",
    },
    pdfContainer: {
      height: "100%",
    },
    htmlContainer: {
      height: "100%",
    },
    iframe: {
      width: "100%",
      height: "100%",
      border: "none",
    },
    bookAction: {
      display: "flex",
      gap: "10px",
    },
  };

  if (pdfLoading) return <>Loading...</>;

  return (
    <>
      <div className="book-modal-header">
        <h6>Price Book</h6>
        {pdfUrls != undefined && pdfUrls.length > 0 && (
          <div className="book-action">
            <div className="download-ico" onClick={() => downloadPDF()}>
              <i className="icon-download-ico"></i>
            </div>
            <div className="share-ico" onClick={openNewTab}>
              <i className="icon-open-pdf"></i>
            </div>
            <div
              className="generate-ico d-none"
              onClick={() =>
                generateAndDownloadNewPdf("Your dynamic content here")
              }
            >
              <i className="icon-generate-pdf"></i>
            </div>
            {props.priceBookFile.length > 0 &&
              <div
                className="mobile-resp"
                onClick={() => setIsShowPriceBook && setIsShowPriceBook(false)}
              >
                <i className="icon-close"></i>
              </div>
            }
          </div>
        )}
        {props.priceBookFile.length > 0 && (
          <div
            className="price-book-close"
            onClick={() => setIsShowPriceBook && setIsShowPriceBook(false)}
          >
            <span>Close</span>
            <i className="icon-close"></i>
          </div>
        )}
      </div>
      <div className="book-modal-body" style={inlineStyles.bookModalBody}>
        <div className="pdf-viewer" style={inlineStyles.pdfViewer}>
          {error && <div className="error">{error}</div>}
          {pdfUrls.length === 0 && !error && !Object.keys(content).length && (
            <div className="loading">Loading content...</div>
          )}
          {pdfUrls.length > 0 &&
            pdfUrls.map((pdf, i) => (
              <div
                key={i}
                className="pdf-container"
                style={inlineStyles.pdfContainer}
              >
                <iframe
                  // ref={iframeRef}
                  src={`https://docs.google.com/gview?url=${encodeURIComponent(
                    pdf
                  )}&embedded=true`}
                  style={inlineStyles.iframe}
                  title={`pdf-viewer-${i}`}
                />
              </div>
            ))}
          {Object.keys(content).map((index) => (
            <div
              key={index}
              className="html-container"
              style={inlineStyles.htmlContainer}
            >
              <iframe
                srcDoc={htmlContents[index]}
                style={inlineStyles.iframe}
                title={`html-content-${index}`}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default PriceBook;
